import { deriveCentralScreenCounts, deriveScreenMapping, getControlTemperatureSensorTrace, getScreenDeviceProduct, getSensorDeviceProduct, getSupplyAirOffCoilSensorTrace, getUfhLimitSensorTrace } from "@automatry/rules";
import { ENGINE_TIMESTAMP, NO_RISK, uniqueSourceRefs } from "./strategyContext.js";
import { INTERFACE_DEMAND_POLICY_FLAG_FAMILY_IDS, bedroomTouch4CountForTopology } from "./controlTopologySelector.js";
export function deriveDeviceMappings(input) {
    const sourceRefs = uniqueSourceRefs([
        ...input.sourceRefs,
        ...input.zoneModel.sourceRefs
    ]);
    const devices = [];
    const deviceMappings = [];
    const mappingScheduleRows = [];
    const commissioningTasks = [];
    const seenDeviceIds = new Set();
    const screenDerivation = deriveScreenDevices({
        ...input,
        sourceRefs
    });
    const sensorDerivation = deriveSensorDevices({
        ...input,
        centralScreenRequired: input.centralScreenRequired,
        sourceProductQuantities: input.sourceProductQuantities,
        sourceRefs
    });
    const meteringDerivation = deriveMeteringDevices({
        apartmentTypeId: input.apartmentTypeId,
        meteringTopology: input.meteringTopology,
        meterProducts: input.meteringMeterProducts,
        sourceRefs
    });
    const interfaceDemandDerivation = deriveInterfaceDemandDevices({
        projectId: input.projectId,
        apartmentTypeId: input.apartmentTypeId,
        takeoffRows: input.takeoffRows,
        zoneModel: input.zoneModel,
        sourceRefs,
        ...(input.policyFlagSet ? { policyFlagSet: input.policyFlagSet } : {})
    });
    for (const item of [
        ...screenDerivation,
        ...sensorDerivation,
        ...meteringDerivation,
        ...interfaceDemandDerivation.items
    ]) {
        if (!seenDeviceIds.has(item.device.id)) {
            devices.push(item.device);
            seenDeviceIds.add(item.device.id);
        }
        deviceMappings.push(item.mapping);
        mappingScheduleRows.push(makeMappingScheduleRow({
            apartmentTypeId: input.apartmentTypeId,
            device: item.device,
            mapping: item.mapping,
            room: item.zone?.roomName ?? item.zone?.label
        }));
        commissioningTasks.push(makeCommissioningTask({
            projectId: input.projectId,
            device: item.device,
            mapping: item.mapping,
            zone: item.zone
        }));
    }
    const ruleTraceRefs = [
        ...new Set([
            ...devices.flatMap((device) => device.ruleTraceRefs),
            ...deviceMappings.flatMap((mapping) => mapping.ruleTraceRefs),
            ...interfaceDemandDerivation.risks.flatMap((risk) => risk.ruleTraceRefs)
        ])
    ];
    return {
        devices,
        deviceMappings,
        mappingScheduleRows,
        commissioningTasks,
        risks: interfaceDemandDerivation.risks,
        ruleTraceRefs
    };
}
const INTERFACE_DEMAND_DEFINITIONS = [
    {
        familyId: "interface-demand.hiu-interface",
        role: "heat_interface_unit",
        productPartNo: "BY-OTHERS-HIU",
        label: "heat interface unit",
        count: (rows) => ({
            count: sumTakeoffCount(rows, "hiuCount"),
            basis: "hiuCount"
        }),
        pointDemandRefs: () => []
    },
    {
        familyId: "interface-demand.ciu-interface",
        role: "cooling_interface_unit",
        productPartNo: "BY-OTHERS-CIU",
        label: "cooling interface unit",
        count: (rows) => ({
            count: sumTakeoffCount(rows, "ciuCount"),
            basis: "ciuCount"
        }),
        pointDemandRefs: () => []
    },
    {
        familyId: "interface-demand.wet-ufh-actuator-interface",
        role: "wet_ufh_actuator_output",
        productPartNo: "BY-OTHERS-WET-UFH-ACTUATOR",
        label: "wet UFH actuator output",
        count: wetActuatorTakeoffCount,
        pointDemandRefs: (zoneModel) => zoneModel.pointDemands
            .filter((demand) => demand.demandKind.includes("wufh"))
            .map((demand) => demand.id)
    },
    {
        familyId: "interface-demand.electric-ufh-zone-interface",
        role: "electric_ufh_zone_output",
        productPartNo: "BY-OTHERS-EUFH-ZONE",
        label: "electric UFH zone output",
        count: (rows) => ({
            count: sumTakeoffCount(rows, "eufhZoneCount"),
            basis: "eufhZoneCount"
        }),
        pointDemandRefs: (zoneModel) => zoneModel.pointDemands
            .filter((demand) => demand.demandKind.includes("eufh"))
            .map((demand) => demand.id)
    },
    {
        familyId: "interface-demand.towel-rail-interface",
        role: "towel_rail_output",
        productPartNo: "BY-OTHERS-TOWEL-RAIL",
        label: "towel rail output",
        count: (rows) => ({
            count: sumTakeoffCount(rows, "towelRailCount"),
            basis: "towelRailCount"
        }),
        pointDemandRefs: (zoneModel) => zoneModel.pointDemands
            .filter((demand) => demand.demandKind.includes("towel_rail"))
            .map((demand) => demand.id)
    }
];
function deriveInterfaceDemandDevices(input) {
    const rows = input.takeoffRows.filter((row) => row.apartmentTypeId === input.apartmentTypeId && rowHasAcceptedTakeoffSurface(row));
    const items = [];
    const risks = [];
    for (const definition of INTERFACE_DEMAND_DEFINITIONS) {
        const flagDecision = resolveInterfaceDemandFlag({
            familyId: definition.familyId,
            projectId: input.projectId,
            apartmentTypeId: input.apartmentTypeId,
            ...(input.policyFlagSet ? { policyFlagSet: input.policyFlagSet } : {})
        });
        if (flagDecision?.conflict) {
            risks.push(makeInterfaceDemandConflictRisk({
                projectId: input.projectId,
                apartmentTypeId: input.apartmentTypeId,
                familyId: definition.familyId,
                flagIds: flagDecision.flagIds,
                sourceRefs: uniqueSourceRefs([
                    ...input.sourceRefs,
                    ...flagDecision.evidenceRefs
                ])
            }));
            continue;
        }
        if (flagDecision?.enabled !== true) {
            continue;
        }
        const countResult = definition.count(rows);
        if (countResult.count <= 0) {
            continue;
        }
        items.push(makeInterfaceDemandDeviceAndMapping({
            apartmentTypeId: input.apartmentTypeId,
            definition,
            quantity: countResult.count,
            countBasis: countResult.basis,
            pointDemandRefs: definition.pointDemandRefs(input.zoneModel),
            sourceRefs: input.sourceRefs,
            flagResolution: flagDecision.resolution
        }));
    }
    return { items, risks };
}
function makeInterfaceDemandDeviceAndMapping(input) {
    const deviceId = `device-${input.apartmentTypeId}-${input.definition.role}`;
    const ruleTraceRefs = [`trace-interface-demand-${input.definition.role}`];
    const flagText = input.flagResolution === "default_true"
        ? "the default-true interface-demand policy is in force"
        : "the reviewed interface-demand policy resolves true";
    const device = {
        id: deviceId,
        productPartNo: input.definition.productPartNo,
        family: "By-others mechanical interface demand",
        role: input.definition.role,
        quantity: input.quantity,
        pointDemandRefs: input.pointDemandRefs,
        why: `${input.quantity} ${input.definition.label}(s) are present in the accepted takeoff and ${flagText}.`,
        how: `${input.countBasis} drives this A4 interface-demand row. The row records the controls interface/output demand only; the physical plant unit remains by others and is not converted into a Titan-supplied controller or infrastructure product.`,
        sourceRefs: [...input.sourceRefs],
        ruleTraceRefs,
        risk: NO_RISK
    };
    const mapping = {
        id: `device-map-${deviceId}`,
        deviceRef: device.id,
        physicalTerminationRef: `interface-demand-${input.apartmentTypeId}-${input.definition.role}`,
        mappingType: "central_io",
        cableImplication: "Controls coordination point only; final plant wiring/interface terminal is confirmed against the by-others mechanical package.",
        commissioningNote: `Verify ${input.definition.label} interface demand quantity ${input.quantity} against the latest accepted takeoff and commissioning schedule.`,
        why: "A4 interface-demand rows must be visible to mapping, commissioning, export, and parity scoring.",
        how: "Mapped as a central coordination interface without changing controller zone allocation or hard-IO capacity.",
        sourceRefs: [...input.sourceRefs],
        ruleTraceRefs,
        risk: NO_RISK
    };
    return { device, mapping };
}
function resolveInterfaceDemandFlag(input) {
    if (!INTERFACE_DEMAND_POLICY_FLAG_FAMILY_IDS.includes(input.familyId)) {
        return undefined;
    }
    const candidates = (input.policyFlagSet?.activeFlags ?? [])
        .filter((flag) => flag.status !== "superseded" &&
        flag.familyId === input.familyId &&
        flag.value.kind === "boolean" &&
        "value" in flag.value &&
        typeof flag.value.value === "boolean" &&
        flagAppliesToApartmentType(flag, input))
        .map((flag) => ({
        flag,
        rank: policyFlagAuthorityRank(flag.provenance) + policyFlagScopeRank(flag.scope)
    }))
        .sort((left, right) => right.rank === left.rank
        ? left.flag.id.localeCompare(right.flag.id)
        : right.rank - left.rank);
    const [winner] = candidates;
    if (!winner) {
        return {
            enabled: true,
            flagIds: [],
            evidenceRefs: [],
            conflict: false,
            resolution: "default_true"
        };
    }
    const topRanked = candidates.filter((candidate) => candidate.rank === winner.rank);
    const topValues = new Set(topRanked.map((candidate) => candidate.flag.value.value));
    const evidenceRefs = uniqueSourceRefs(topRanked.flatMap((candidate) => candidate.flag.evidenceRefs ?? []));
    const flagIds = topRanked.map((candidate) => candidate.flag.id);
    if (topValues.size > 1) {
        return {
            conflict: true,
            flagIds,
            evidenceRefs
        };
    }
    return {
        enabled: winner.flag.value.value === true,
        flagIds,
        evidenceRefs,
        resolution: winner.flag.value.value === true ? "policy_true" : "policy_false",
        conflict: false
    };
}
function rowHasAcceptedTakeoffSurface(row) {
    return (row.sourceRefs ?? []).some((sourceRef) => ["takeoff", "drawing"].includes(sourceRef.authorityClass));
}
function flagAppliesToApartmentType(flag, input) {
    const scope = flag.scope;
    if (!scope) {
        return true;
    }
    if (scope.projectId !== input.projectId) {
        return false;
    }
    if (scope.level === "apartment-type" || scope.level === "room") {
        return scope.apartmentTypeId === input.apartmentTypeId;
    }
    if (scope.level === "item-class" && scope.apartmentTypeId !== undefined) {
        return scope.apartmentTypeId === input.apartmentTypeId;
    }
    return true;
}
function policyFlagAuthorityRank(provenance) {
    switch (provenance) {
        case "user-override":
        case "user-decision":
            return 500;
        case "ai-extracted":
        case "provider-conflict":
            return 400;
        case "org-default":
            return 200;
        case "standard-default":
            return 100;
        default:
            return 300;
    }
}
function policyFlagScopeRank(scope) {
    if (!scope) {
        return 0;
    }
    switch (scope.level) {
        case "room":
            return 50;
        case "apartment-type":
            return 40;
        case "item-class":
            return scope.apartmentTypeId ? 35 : 20;
        case "requirement":
            return 30;
        case "project":
            return 10;
    }
}
function makeInterfaceDemandConflictRisk(input) {
    const ruleTraceRefs = ["trace-interface-demand-policy-conflict"];
    return {
        id: `risk-${input.apartmentTypeId}-${safeId(input.familyId)}-conflict`,
        projectId: input.projectId,
        type: "needsUserDecision",
        tags: {},
        severity: "warning",
        scope: {
            level: "apartment_type",
            refId: input.apartmentTypeId
        },
        title: "Interface-demand policy conflict needs confirmation",
        plainEnglish: `${input.familyId} has conflicting active policy flags (${input.flagIds.join(", ")}). The engine did not silently pick an interface-demand row.`,
        technicalTrace: [...input.sourceRefs],
        recommendedActions: [
            {
                action: "Resolve the conflicting interface-demand policy flags and rerun specification.",
                owner: "estimator",
                status: "open"
            }
        ],
        status: "open",
        blocksExport: false,
        sourceRefs: [...input.sourceRefs],
        ruleTraceRefs,
        createdAt: ENGINE_TIMESTAMP
    };
}
function sumTakeoffCount(rows, field) {
    return rows.reduce((total, row) => total + (row[field] ?? 0), 0);
}
function wetActuatorTakeoffCount(rows) {
    if (rows.some((row) => row.wetActuatorCount !== undefined)) {
        if (rows.every((row) => wetActuatorCountIsReviewedZoneFallback(row))) {
            return {
                count: sumTakeoffCount(rows, "wetActuatorCount"),
                basis: "wufhZoneCount fallback"
            };
        }
        return {
            count: sumTakeoffCount(rows, "wetActuatorCount"),
            basis: "wetActuatorCount"
        };
    }
    const rawWetActuatorValues = rows
        .map((row) => rawNumber(row.rawFields, "wetActuatorsPerApartment"))
        .filter((value) => value !== undefined);
    if (rawWetActuatorValues.length > 0) {
        return {
            count: rawWetActuatorValues.reduce((total, value) => total + value, 0),
            basis: "rawFields.wetActuatorsPerApartment"
        };
    }
    return {
        count: sumTakeoffCount(rows, "wufhZoneCount"),
        basis: "wufhZoneCount fallback"
    };
}
function wetActuatorCountIsReviewedZoneFallback(row) {
    return (row.wetActuatorCount !== undefined &&
        row.wetActuatorCount === row.wufhZoneCount &&
        (row.normalizedFrom ?? []).some((normalization) => normalization.canonicalField === "wetActuatorCount" &&
            normalization.rawHeading.toLowerCase().includes("fallback")));
}
function rawNumber(rawFields, key) {
    if (!rawFields || typeof rawFields !== "object" || Array.isArray(rawFields)) {
        return undefined;
    }
    const value = rawFields[key];
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
}
function deriveMeteringDevices(input) {
    if (input.meteringTopology !== "netmaster_direct") {
        return [];
    }
    return input.meterProducts.map((meterProduct) => makeByOthersMeterDeviceAndMapping({
        apartmentTypeId: input.apartmentTypeId,
        meterProduct,
        sourceRefs: input.sourceRefs
    }));
}
function makeByOthersMeterDeviceAndMapping(input) {
    const productKey = safeId(input.meterProduct.productPartNo);
    const deviceId = `device-${input.apartmentTypeId}-${productKey}-by-others`;
    const ruleTraceRefs = ["trace-metering-netmaster-direct-by-others-meters"];
    const device = {
        id: deviceId,
        productPartNo: input.meterProduct.productPartNo,
        family: input.meterProduct.family,
        role: input.meterProduct.role,
        quantity: input.meterProduct.quantity,
        pointDemandRefs: [],
        why: "A known by-others meter product is part of the NetMaster-direct Modbus metering bus.",
        how: "Derived from approved metering evidence naming the meter product on a Modbus RTU RS485 bus read by TP-NM-R-EM-D; it is emitted as a BOM/scope line and does not consume controller hard IO.",
        sourceRefs: input.sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    const mapping = {
        id: `device-map-${deviceId}`,
        deviceRef: device.id,
        mappingType: "network_pairing",
        cableImplication: input.meterProduct.connectivity,
        commissioningNote: "Confirm Modbus address, baud/parity/stop-bit settings, and live values from this by-others meter at the NetMaster during commissioning.",
        why: "The meter communicates over the metering bus rather than landing as a hardwired controller point.",
        how: "Mapped as a network/bus pairing to keep Modbus meters out of IO-COM-EM8 hardwired point sizing.",
        sourceRefs: input.sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    return {
        device,
        mapping
    };
}
function safeId(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}
function deriveScreenDevices(input) {
    const thermostatCount = getMarkedThermostatCount(input);
    const uiTargets = buildUiTargets(input.zoneModel, thermostatCount);
    const selector = input.controlTopologySelector;
    if (selector?.mode === "central_screen_is_stat_for_own_zone") {
        return deriveCentralScreenAsZoneStatDevices({
            ...input,
            thermostatCount,
            uiTargets,
            selector
        });
    }
    const central = deriveCentralScreenCounts({
        thermostatCount,
        controllerCount: input.controllers.length,
        centralScreenRequired: input.centralScreenRequired,
        countedThermostatAtCentralLocation: false,
        uiStrategy: input.uiStrategy
    });
    const result = [];
    if (central.replacementMode === "central_only") {
        return makeCentralOnlyScreenDeviceAndMappings({
            apartmentTypeId: input.apartmentTypeId,
            zoneModel: input.zoneModel,
            controllers: input.controllers,
            sourceRefs: input.sourceRefs,
            extraRuleTraceRefs: central.traces.map((trace) => trace.id)
        });
    }
    const replacementZone = central.replacementMode === "replaces_counted_thermostat"
        ? uiTargets[0]
        : undefined;
    const localTargets = central.replacementMode === "replaces_counted_thermostat"
        ? uiTargets.slice(1)
        : uiTargets;
    for (let index = 0; index < central.ts4Count; index += 1) {
        const zone = targetAt(localTargets, index);
        if (!zone) {
            continue;
        }
        result.push(makeScreenDeviceAndMapping({
            apartmentTypeId: input.apartmentTypeId,
            kind: "ts4",
            index,
            zone,
            controller: findControllerForZone(zone, input.zoneModel, input.controllers),
            pointDemandRefs: getPointDemandRefsForZone(zone, input.zoneModel),
            sourceRefs: input.sourceRefs,
            extraRuleTraceRefs: central.traces.map((trace) => trace.id)
        }));
    }
    if (central.ts7Count > 0) {
        result.push(makeScreenDeviceAndMapping({
            apartmentTypeId: input.apartmentTypeId,
            kind: "ts7",
            index: 0,
            zone: replacementZone,
            controller: replacementZone
                ? findControllerForZone(replacementZone, input.zoneModel, input.controllers)
                : input.controllers[0],
            pointDemandRefs: replacementZone
                ? getPointDemandRefsForZone(replacementZone, input.zoneModel)
                : [],
            sourceRefs: input.sourceRefs,
            extraRuleTraceRefs: central.traces.map((trace) => trace.id),
            replacementMode: central.replacementMode
        }));
    }
    return result;
}
function deriveCentralScreenAsZoneStatDevices(input) {
    const result = [];
    const centralZone = input.uiTargets[0];
    const localTargets = centralZone
        ? input.uiTargets.filter((zone) => zone.id !== centralZone.id)
        : input.uiTargets;
    const ts4Count = bedroomTouch4CountForTopology({
        apartmentTypeId: input.apartmentTypeId,
        takeoffRows: input.takeoffRows,
        fallbackThermostatCount: input.thermostatCount
    });
    const selectorSourceRefs = uniqueSourceRefs([
        ...input.sourceRefs,
        ...input.selector.evidenceRefs
    ]);
    const selectorTraceRefs = [
        "trace-control-topology-central-screen-is-stat",
        `trace-policy-flag-${input.selector.policyFlagId}`
    ];
    for (let index = 0; index < ts4Count; index += 1) {
        const zone = targetAt(localTargets, index);
        if (!zone) {
            continue;
        }
        result.push(makeScreenDeviceAndMapping({
            apartmentTypeId: input.apartmentTypeId,
            kind: "ts4",
            index,
            zone,
            controller: findControllerForZone(zone, input.zoneModel, input.controllers),
            pointDemandRefs: getPointDemandRefsForZone(zone, input.zoneModel),
            sourceRefs: selectorSourceRefs,
            extraRuleTraceRefs: selectorTraceRefs,
            selector: input.selector
        }));
    }
    result.push(makeScreenDeviceAndMapping({
        apartmentTypeId: input.apartmentTypeId,
        kind: "ts7",
        index: 0,
        zone: centralZone,
        controller: centralZone
            ? findControllerForZone(centralZone, input.zoneModel, input.controllers)
            : input.controllers[0],
        pointDemandRefs: centralZone
            ? getPointDemandRefsForZone(centralZone, input.zoneModel)
            : [],
        sourceRefs: selectorSourceRefs,
        extraRuleTraceRefs: selectorTraceRefs,
        replacementMode: "replaces_counted_thermostat",
        selector: {
            ...input.selector,
            centralScreen: {
                ...input.selector.centralScreen,
                ...(centralZone
                    ? {
                        ownsZoneId: centralZone.id,
                        location: centralZone.roomName ?? centralZone.label
                    }
                    : {})
            }
        }
    }));
    return result;
}
function deriveSensorDevices(input) {
    const result = [];
    const sensorZones = input.zoneModel.logicalZones.filter(isSensorZone);
    const sourceUfhLimitQuantity = getSourceUfhLimitQuantity(input);
    const explicitThermostatCount = getExplicitThermostatCount(input);
    const dedupeCompositeControlSensors = input.centralScreenRequired &&
        explicitThermostatCount !== undefined &&
        explicitThermostatCount < sensorZones.length;
    if (sourceUfhLimitQuantity &&
        shouldEmitSourceProductQuantity(input, sourceUfhLimitQuantity)) {
        result.push(makeSourceQuantitySensorDeviceAndMapping({
            apartmentTypeId: input.apartmentTypeId,
            sourceProductQuantity: sourceUfhLimitQuantity,
            sourceRefs: input.sourceRefs
        }));
    }
    for (const zone of sensorZones) {
        const controller = findControllerForZone(zone, input.zoneModel, input.controllers);
        if (zone.controlZoneRef === undefined || !dedupeCompositeControlSensors) {
            result.push(makeSensorDeviceAndMapping({
                apartmentTypeId: input.apartmentTypeId,
                sensorKind: "control_temperature",
                zone,
                controller,
                pointDemandRefs: getPointDemandRefsForZone(zone, input.zoneModel),
                sourceRefs: input.sourceRefs
            }));
        }
        if (sourceUfhLimitQuantity === undefined &&
            (zone.zoneKind === "wufh" || zone.zoneKind === "eufh")) {
            result.push(makeSensorDeviceAndMapping({
                apartmentTypeId: input.apartmentTypeId,
                sensorKind: "ufh_limit_active",
                zone,
                controller,
                pointDemandRefs: getPointDemandRefsForZone(zone, input.zoneModel),
                sourceRefs: input.sourceRefs
            }));
            result.push(makeSensorDeviceAndMapping({
                apartmentTypeId: input.apartmentTypeId,
                sensorKind: "ufh_limit_spare",
                zone,
                controller,
                pointDemandRefs: [],
                sourceRefs: input.sourceRefs
            }));
        }
    }
    for (const zone of input.zoneModel.logicalZones.filter(isFcuZone)) {
        result.push(makeSupplyAirSensorDeviceAndMapping({
            apartmentTypeId: input.apartmentTypeId,
            zone,
            controller: findControllerForZone(zone, input.zoneModel, input.controllers),
            sourceRefs: input.sourceRefs
        }));
    }
    return result;
}
function isFcuZone(zone) {
    return zone.zoneKind === "fcu";
}
function getSourceUfhLimitQuantity(input) {
    return input.sourceProductQuantities.find((item) => item.productPartNo === "TPTVAV/L" ||
        item.role === "ufh_limit_sensor" ||
        item.role === "ufh_10m_sensor");
}
function firstApartmentTypeId(takeoffRows) {
    return takeoffRows[0]?.apartmentTypeId;
}
function shouldEmitSourceProductQuantity(input, sourceProductQuantity) {
    return (sourceProductQuantity.provisionScope !== "project" ||
        input.apartmentTypeId === firstApartmentTypeId(input.takeoffRows));
}
function makeScreenDeviceAndMapping(input) {
    const product = getScreenDeviceProduct(input.kind);
    const deviceId = input.kind === "ts7"
        ? `device-${input.apartmentTypeId}-central-ts7`
        : `device-${input.apartmentTypeId}-ts4-${input.index + 1}-${input.zone?.id ?? "unassigned"}`;
    const screenMapping = deriveScreenMapping({
        id: deviceId,
        kind: input.kind,
        zoneId: input.zone?.id ?? input.apartmentTypeId,
        ...(input.controller ? { controllerId: input.controller.id } : {}),
        isCentralControlScreen: input.kind === "ts7"
    });
    const ruleTraceRefs = [
        ...new Set([
            product.ruleTraceId,
            ...screenMapping.traces.map((trace) => trace.id),
            ...input.extraRuleTraceRefs,
            ...(input.zone?.ruleTraceRefs ?? [])
        ])
    ];
    const sourceRefs = uniqueSourceRefs([
        ...input.sourceRefs,
        ...(input.zone?.sourceRefs ?? [])
    ]);
    const replacementText = input.replacementMode === "replaces_counted_thermostat"
        ? ` It explicitly replaces the local thermostat/UI point in that room${input.selector ? ` through ${input.selector.policyFlagId}` : ""}.`
        : "";
    const device = {
        id: deviceId,
        productPartNo: product.productPartNo,
        family: product.family,
        role: product.role,
        quantity: 1,
        ...(input.zone ? { ownerRef: input.zone.id } : {}),
        pointDemandRefs: input.pointDemandRefs,
        why: input.kind === "ts7"
            ? `A central control requirement derives a TS-7 master screen.${replacementText}`
            : input.selector?.mode === "central_screen_is_stat_for_own_zone"
                ? "The control-topology selector down-selects a bedroom Touch-4; bathrooms/en-suites and other positions do not become separate Touch-4 screens in this mode."
                : "Each marked thermostat/UI point derives a default TS-4 local screen.",
        how: input.kind === "rdu"
            ? "Mapped through @automatry/rules RDU direct-wired topology."
            : input.selector
                ? `Mapped through the F1 control-topology selector (${input.selector.mode}): ${input.selector.why}`
                : "Mapped through @automatry/rules networked TS-4/TS-7 screen topology.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    const mapping = {
        id: `device-map-${deviceId}`,
        deviceRef: device.id,
        ...(input.controller ? { logicalControllerRef: input.controller.id } : {}),
        ...(input.zone ? { zoneId: input.zone.id, roomId: input.zone.id } : {}),
        mappingType: screenMapping.mappingType,
        cableImplication: screenMapping.mappingType === "direct_wired"
            ? "RDU-Touch is hard-wired 1:1 to the controller serving this zone."
            : "TS-4/TS-7 is networked and paired in software; it is not a 1:1 controller wiring constraint.",
        commissioningNote: screenMapping.mappingType === "direct_wired"
            ? "Verify the RDU direct cable lands at the controller serving this zone."
            : `Pair ${product.productPartNo} to ${input.controller?.id ?? "the selected controller"} for the served zone during commissioning.`,
        why: screenMapping.mappingType === "direct_wired"
            ? "RDU topology is a hard wiring constraint."
            : "Network screens can pair to any controller, so the controller reference is a logical pairing, not a hardwired termination.",
        how: "Built from @automatry/rules deriveScreenMapping for the derived UI device.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    return {
        device,
        mapping,
        ...(input.zone ? { zone: input.zone } : {})
    };
}
function makeCentralOnlyScreenDeviceAndMappings(input) {
    const zones = input.zoneModel.logicalZones.filter(isUiZone);
    if (zones.length === 0) {
        return [
            makeScreenDeviceAndMapping({
                apartmentTypeId: input.apartmentTypeId,
                kind: "ts7",
                index: 0,
                controller: input.controllers[0],
                pointDemandRefs: [],
                sourceRefs: input.sourceRefs,
                extraRuleTraceRefs: input.extraRuleTraceRefs,
                replacementMode: "central_only"
            })
        ];
    }
    const product = getScreenDeviceProduct("ts7");
    const deviceId = `device-${input.apartmentTypeId}-central-ts7`;
    const pointDemandRefs = [
        ...new Set(zones.flatMap((zone) => getPointDemandRefsForZone(zone, input.zoneModel)))
    ];
    const deviceSourceRefs = uniqueSourceRefs([
        ...input.sourceRefs,
        ...zones.flatMap((zone) => zone.sourceRefs)
    ]);
    const deviceRuleTraceRefs = [
        ...new Set([
            product.ruleTraceId,
            "trace-ts7-network-screen",
            "trace-central-screen-controls-all-zones",
            ...input.extraRuleTraceRefs,
            ...zones.flatMap((zone) => zone.ruleTraceRefs)
        ])
    ];
    const device = {
        id: deviceId,
        productPartNo: product.productPartNo,
        family: product.family,
        role: product.role,
        quantity: 1,
        pointDemandRefs,
        why: "A central-only UI requirement derives one TS-7/CCS as the sole apartment UI for every controlled zone.",
        how: "The central screen is network-paired to all zone/controller assignments after controller packing; it does not create per-room TS-4/RDU devices.",
        sourceRefs: deviceSourceRefs,
        ruleTraceRefs: deviceRuleTraceRefs,
        risk: NO_RISK
    };
    return zones.map((zone) => {
        const controller = findControllerForZone(zone, input.zoneModel, input.controllers);
        const sourceRefs = uniqueSourceRefs([...deviceSourceRefs, ...zone.sourceRefs]);
        const ruleTraceRefs = [...new Set([...deviceRuleTraceRefs, ...zone.ruleTraceRefs])];
        const mapping = {
            id: `device-map-${deviceId}-${zone.id}`,
            deviceRef: device.id,
            ...(controller ? { logicalControllerRef: controller.id } : {}),
            roomId: zone.id,
            zoneId: zone.id,
            mappingType: "network_pairing",
            cableImplication: "Central TS-7/CCS is networked; the controller reference is the logical zone owner, not a local UI wiring constraint.",
            commissioningNote: `Pair central TS-7/CCS to ${controller?.id ?? "the selected controller"} for ${zone.label}; verify central-only UI controls this zone and no per-room TS-4/RDU is installed.`,
            why: "Central-only strategy maps one apartment screen to every controlled zone.",
            how: "The screen abstracts first/second-zone controller placement; zone-controller ownership comes from the packed controller schedule.",
            sourceRefs,
            ruleTraceRefs,
            risk: NO_RISK
        };
        return {
            device,
            mapping,
            zone
        };
    });
}
function makeSensorDeviceAndMapping(input) {
    const product = getSensorDeviceProduct(input.sensorKind);
    const sensorTrace = input.sensorKind === "control_temperature"
        ? getControlTemperatureSensorTrace()
        : getUfhLimitSensorTrace();
    const isTerminated = input.sensorKind === "control_temperature" ||
        (input.sensorKind === "ufh_limit_active" && input.zone.zoneKind === "wufh");
    const deviceId = `device-${input.apartmentTypeId}-${input.sensorKind}-${input.zone.id}`;
    const ruleTraceRefs = [
        ...new Set([product.ruleTraceId, sensorTrace.id, ...input.zone.ruleTraceRefs])
    ];
    const sourceRefs = uniqueSourceRefs([...input.sourceRefs, ...input.zone.sourceRefs]);
    const mappingType = isTerminated ? "direct_wired" : "not_terminated_spare";
    const device = {
        id: deviceId,
        productPartNo: product.productPartNo,
        family: product.family,
        role: product.role,
        quantity: 1,
        ownerRef: input.zone.id,
        pointDemandRefs: input.pointDemandRefs,
        ...(product.valueEngineering ? { valueEngineering: product.valueEngineering } : {}),
        why: input.sensorKind === "control_temperature"
            ? "Every controllable zone needs a dedicated control-temperature sensor."
            : "UFH limit sensors are supplied as physical devices separately from room control sensing.",
        how: input.sensorKind === "ufh_limit_spare"
            ? "Derived as a spare/redundant UFH limit sensor that does not consume controller IO."
            : "Derived from the Baseline sensor and UFH rules in @automatry/rules.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    const mapping = {
        id: `device-map-${deviceId}`,
        deviceRef: device.id,
        ...(input.controller ? { logicalControllerRef: input.controller.id } : {}),
        ...(isTerminated && input.controller
            ? { physicalTerminationRef: input.controller.id }
            : {}),
        roomId: input.zone.id,
        zoneId: input.zone.id,
        mappingType,
        cableImplication: isTerminated
            ? "Sensor terminates on the controller UI/input already accounted for by the zone point demand."
            : "Sensor is supplied/installed but must not be landed as controller UI for this mapping.",
        commissioningNote: isTerminated
            ? "Verify sensor reading at the assigned controller input and confirm it is associated with the served zone."
            : "Record the supplied UFH limit sensor and confirm it is not counted as controller UI.",
        why: isTerminated
            ? "Control sensing must be wired to the controller/IO serving the zone."
            : "Spare and EUFH limit sensors are physical devices but not controller UI terminations.",
        how: "Mapped from the sensor device policy in @automatry/rules without changing point-ledger demand.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    return {
        device,
        mapping,
        zone: input.zone
    };
}
function makeSupplyAirSensorDeviceAndMapping(input) {
    const product = getSensorDeviceProduct("supply_air_off_coil");
    const sensorTrace = getSupplyAirOffCoilSensorTrace();
    const deviceId = `device-${input.apartmentTypeId}-supply-air-off-coil-${input.zone.id}`;
    const ruleTraceRefs = [
        ...new Set([product.ruleTraceId, sensorTrace.id, ...input.zone.ruleTraceRefs])
    ];
    const sourceRefs = uniqueSourceRefs([...input.sourceRefs, ...input.zone.sourceRefs]);
    const device = {
        id: deviceId,
        productPartNo: product.productPartNo,
        family: product.family,
        role: product.role,
        quantity: 1,
        ownerRef: input.zone.id,
        pointDemandRefs: [],
        why: "Each FCU zone gets a supply-air/off-coil sensor as the default higher-quality install.",
        how: "Derived from the modelled FCU-zone count. The sensor improves Edge anomaly detection and is a BOM/mapping item only; it does not alter controller point-ledger demand.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    const mapping = {
        id: `device-map-${deviceId}`,
        deviceRef: device.id,
        ...(input.controller ? { logicalControllerRef: input.controller.id } : {}),
        ...(input.controller ? { physicalTerminationRef: input.controller.id } : {}),
        roomId: input.zone.id,
        zoneId: input.zone.id,
        mappingType: "direct_wired",
        cableImplication: "Supply-air/off-coil sensor is fitted at the FCU and associated with the serving controller; this default does not change the controller point ledger.",
        commissioningNote: "Confirm the TPTVAV/S off-coil temperature reading at commissioning, or remove/suppress this default if supply-air sensing is not required.",
        why: "Off-coil temperature improves diagnostics for the FCU served by this zone.",
        how: "Mapped one-to-one from FCU zone to serving controller after controller packing; no return-air sensor is inferred.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    return {
        device,
        mapping,
        zone: input.zone
    };
}
function makeSourceQuantitySensorDeviceAndMapping(input) {
    const product = getSensorDeviceProduct("ufh_limit_active");
    const sensorTrace = getUfhLimitSensorTrace();
    const sourceRefs = uniqueSourceRefs([
        ...input.sourceRefs,
        ...input.sourceProductQuantity.sourceRefs
    ]);
    const productKey = input.sourceProductQuantity.productPartNo
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    const deviceId = `device-${input.apartmentTypeId}-${productKey}-source-quantity`;
    const ruleTraceRefs = [
        product.ruleTraceId,
        sensorTrace.id,
        "trace-ufh-limit-sensors-source-quantity"
    ];
    const device = {
        id: deviceId,
        productPartNo: input.sourceProductQuantity.productPartNo,
        family: product.family,
        role: input.sourceProductQuantity.role ?? "ufh_limit_sensor_source_quantity",
        quantity: input.sourceProductQuantity.quantity,
        provisionScope: input.sourceProductQuantity.provisionScope,
        pointDemandRefs: [],
        why: "An approved source gives an explicit UFH limit sensor product quantity for the project.",
        how: "The source quantity is used instead of expanding one active plus one spare UFH limit sensor for every modelled UFH zone.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    const mapping = {
        id: `device-map-${deviceId}`,
        deviceRef: device.id,
        mappingType: "not_terminated_spare",
        cableImplication: "Source-counted UFH limit sensors are compared as a delivered BOM quantity; individual terminations remain a commissioning/detail design activity.",
        commissioningNote: "Reconcile the source-counted UFH limit sensor quantity against the latest project sensor schedule before installation.",
        why: "The source provides a project-level product quantity rather than one row per controllable UFH zone.",
        how: "Mapped as a non-terminated aggregate product quantity so it does not alter controller UI demand.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
    return {
        device,
        mapping
    };
}
function makeMappingScheduleRow(input) {
    return {
        id: `mapping-row-${input.mapping.id}`,
        apartmentTypeId: input.apartmentTypeId,
        deviceInstanceId: input.device.id,
        ...(input.mapping.logicalControllerRef
            ? { logicalControllerRef: input.mapping.logicalControllerRef }
            : {}),
        ...(input.mapping.physicalTerminationRef
            ? { physicalTerminationRef: input.mapping.physicalTerminationRef }
            : {}),
        ...(input.room ? { room: input.room } : {}),
        ...(input.mapping.cableImplication
            ? { cableImplication: input.mapping.cableImplication }
            : {}),
        commissioningNote: input.mapping.commissioningNote,
        riskRefs: input.mapping.risk.riskRefs,
        sourceRefs: input.mapping.sourceRefs,
        ruleTraceRefs: input.mapping.ruleTraceRefs
    };
}
function makeCommissioningTask(input) {
    const zoneText = input.zone?.label ?? input.zone?.id ?? "apartment";
    return {
        id: `commission-${input.mapping.id}`,
        projectId: input.projectId,
        title: `Commission ${input.device.productPartNo} for ${zoneText}`,
        description: input.mapping.commissioningNote,
        deviceInstanceIds: [input.device.id],
        status: "planned",
        riskRefs: input.mapping.risk.riskRefs,
        sourceRefs: input.mapping.sourceRefs,
        ruleTraceRefs: input.mapping.ruleTraceRefs
    };
}
function getMarkedThermostatCount(input) {
    const explicitCount = getExplicitThermostatCount(input);
    if (explicitCount !== undefined) {
        return explicitCount;
    }
    return getPrimaryUiZones(input.zoneModel).length;
}
function getExplicitThermostatCount(input) {
    const rows = input.takeoffRows.filter((row) => row.apartmentTypeId === input.apartmentTypeId);
    const hasExplicitCount = rows.some((row) => row.thermostatUiPointCount !== undefined);
    if (!hasExplicitCount) {
        return undefined;
    }
    return rows.reduce((total, row) => total + (row.thermostatUiPointCount ?? 0), 0);
}
function buildUiTargets(zoneModel, targetCount) {
    const primaryTargets = getPrimaryUiZones(zoneModel);
    const secondaryTargets = zoneModel.logicalZones.filter((zone) => isUiZone(zone) && !primaryTargets.includes(zone));
    const availableTargets = [...primaryTargets, ...secondaryTargets];
    if (targetCount <= availableTargets.length) {
        return availableTargets.slice(0, targetCount);
    }
    return availableTargets;
}
function targetAt(targets, index) {
    if (targets.length === 0) {
        return undefined;
    }
    return targets[index % targets.length];
}
function getPrimaryUiZones(zoneModel) {
    return zoneModel.logicalZones.filter((zone) => isUiZone(zone) && zone.controlZoneRef === undefined);
}
function isUiZone(zone) {
    return ["fcu", "wufh", "eufh", "heat_only"].includes(zone.zoneKind);
}
function isSensorZone(zone) {
    return isUiZone(zone);
}
function findControllerForZone(zone, zoneModel, controllers) {
    const ownerZoneIds = new Set([zone.id]);
    if (zone.controlZoneRef) {
        ownerZoneIds.add(zone.controlZoneRef);
    }
    for (const candidate of zoneModel.logicalZones) {
        if (candidate.controlZoneRef === zone.id) {
            ownerZoneIds.add(candidate.id);
        }
    }
    return controllers.find((controller) => controller.zoneSlots.some((slot) => ownerZoneIds.has(slot.logicalZoneId)));
}
function getPointDemandRefsForZone(zone, zoneModel) {
    const ownerRefs = new Set([zone.id]);
    for (const candidate of zoneModel.logicalZones) {
        if (candidate.controlZoneRef === zone.id) {
            ownerRefs.add(candidate.id);
        }
    }
    return zoneModel.pointDemands
        .filter((demand) => ownerRefs.has(demand.ownerRef))
        .map((demand) => demand.id);
}
//# sourceMappingURL=deviceMapping.js.map